import React, { useRef, useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import moment from 'moment-timezone';

import { IconChevronRight, IconTrash } from '@tabler/icons';

import api from '../../../services/ApiManagement/Index';
import { useToast } from '../../../hooks/toast';
import { useOnlineStatus } from '../../../hooks/onlineStatus';
import getValidationErrors from '../../../utils/getValidationErrors';

import BackHeader from '../../../components/BackHeader';
import Input from '../../../components/Form/Input';
import Button from '../../../components/Button';
import Accordion from '../../../components/Accordion';
import RadioInput from '../../../components/Form/RadioInput';
import RelativeInput from '../../../components/Animal/RelativeInput';
import ImageInput from '../../../components/Animal/ImageInput';
import SwitchInput from '../../../components/Form/SwitchInput';
import {
  Skeleton,
  Label,
  Spin,
  DeleteButton,
} from '../../../components/Generic';

import {
  Container,
  Content,
  DateInput,
  ImagesWrapper,
  SecondaryButton,
} from './styles';
import Compressor from 'compressorjs';

interface AnimalData {
  sexo: 'macho' | 'femea';
  numero_de_identificacao: string;
  ano: string;
  mes: string;
  dia: string;
  botom_eletronico: string;
  nome: string;
  ra_a: string;
  categoria: string;
  obs: string;
  lote: string;
  pertencente: boolean;
  privado: boolean;
  estado: 'vendido' | 'abatido' | 'morto';
  images: any[];
  pai_id: string;
  mae_id: string;
  safra: string;
}

const EditAnimal: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [animal, setAnimal] = useState<any>({});
  const [images, setImages] = useState<any>([]);

  const formRef = useRef<FormHandles>(null);
  const history = useHistory();
  const isOnline = useOnlineStatus();
  const { addToast } = useToast();
  const { id_property, id_animal }: any = useParams();

  useEffect(() => {
    getAnimal();
  }, []);

  const getAnimal = async () => {
    try {
      setLoading(true);

      const animalResponse = await api.get(
        `/animais/${id_animal}`,
        isOnline,
        'animal',
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      setAnimal(animalResponse.data);
      console.log(animalResponse.data);
      setImages(animalResponse.data.imagens || []);

      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  const handleSubmit = async (data: AnimalData) => {
    try {
      setLoadingAction(true);

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        sexo: Yup.string().required('Campo obrigatório.'),
        ano: Yup.string().required('Campo obrigatório.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await api.put(
        `/animais/${id_animal}`,
        {
          ...animal,
          nome: data.nome,
          data_de_nascimento: `${data.mes} ${data.dia} ${data.ano}`,
          sexo: data.sexo,
          raça: data.ra_a,
          categoria: data.categoria,
          bottom_eletronico:
            data.botom_eletronico?.length === 0
              ? undefined
              : data.botom_eletronico,
          numero_de_identificacao:
            data.numero_de_identificacao.length === 0
              ? ''
              : parseInt(data.numero_de_identificacao, 10),
          pertencente: data.pertencente,
          privado: data.privado,
          lote: data.lote,
          pai_id: data.pai_id.length === 0 ? null : data.pai_id,
          mae_id: data.mae_id.length === 0 ? null : data.mae_id,
          safra: data.safra,
          propriedade_id: id_property,
        },
        isOnline,
        {
          headers: {
            propriedade_id: id_property,
          },
        },
      );

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'O animal foi atualizado.',
      });

      history.goBack();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);

        formRef.current?.setErrors(errors);
        return;
      }

      console.log(err);

      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response?.data?.message || 'Algo deu errado!',
      });
    } finally {
      setLoadingAction(false);
    }
  };

  const handleAddImage = async (e: any) => {
    const file = e.target?.files?.[0];

    if (file) {
      try {
        setLoadingImage(true);

        new Compressor(file, {
          quality: 0.6,
          maxHeight: 700,
          maxWidth: 700,
          success: async resultBlob => {
            const compressedFile = new File([resultBlob], file.name, {
              type: 'image/jpeg',
            });

            const imageData = new FormData();
            imageData.append('imagens', compressedFile);

            await api.patch(
              `/imagens/animal/${animal.id}`,
              imageData,
              isOnline,
              {
                headers: {
                  propriedade_id: id_property,
                },
              },
            );

            addToast({
              type: 'success',
              title: 'Sucesso!',
              description: 'Imagem adicionada ao animal.',
            });

            getAnimal();
          },
          error(err) {
            console.log(err);

            addToast({
              type: 'error',
              title: 'Erro!',
              description: 'Erro ao carregar imagem.',
            });
          },
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado.',
        });
      } finally {
        setLoadingImage(false);
      }
    }
  };

  const removeImage = async (image_id: any) => {
    try {
      await api.delete(`/imagens/${image_id}`, isOnline, {
        headers: {
          propriedade_id: id_property,
        },
      });

      addToast({
        type: 'success',
        title: 'Sucesso!',
        description: 'Imagem deletada.',
      });

      getAnimal();
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro!',
        description: err.response.data.message || 'Algo deu errado.',
      });
    }
  };

  const handleDelete = async () => {
    if (
      window.confirm('Tem certeza? A exclusão de um animal é irreversível.')
    ) {
      try {
        await api.delete(`/animais/${id_animal}`, isOnline, {
          headers: {
            propriedade_id: id_property,
          },
        });

        addToast({
          type: 'success',
          title: 'Sucesso!',
          description: 'Animal deletado.',
        });

        history.go(-2);
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro!',
          description: err.response.data.message || 'Algo deu errado.',
        });
      }
    }
  };

  const radioOptions = [
    { id: 'macho', value: 'macho', label: 'Macho' },
    { id: 'femea', value: 'femea', label: 'Fêmea' },
  ];

  return (
    <Container>
      <BackHeader title="Editar animal" />

      {loading ? (
        <Content>
          <Skeleton style={{ height: 250, width: '100%', marginBottom: 30 }} />
          <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
          <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
          <Skeleton style={{ height: 60, width: '100%', marginBottom: 20 }} />
          <Skeleton style={{ height: 40, width: '100%', marginBottom: 20 }} />
          <Skeleton style={{ height: 20, width: 100, marginBottom: 10 }} />
          <Skeleton style={{ height: 150, width: '100%', marginBottom: 30 }} />
        </Content>
      ) : (
        <Content>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <ImagesWrapper>
              <ImageInput
                images={images}
                onAddImage={handleAddImage}
                removeImage={removeImage}
              />
              {loadingImage && (
                <Spin
                  style={{
                    position: 'absolute',
                    top: 10,
                    left: 10,
                  }}
                />
              )}
            </ImagesWrapper>

            <Input
              label="Número de identificação"
              name="numero_de_identificacao"
              type="number"
              defaultValue={animal.numero_de_identificacao}
              required={true}
            />

            <Input
              label="Botom eletrônico"
              name="botom_eletronico"
              type="text"
              defaultValue={animal.bottom_eletronico}
            />

            <Label>Nascimento</Label>

            <DateInput>
              <Input
                label="Dia"
                name="dia"
                type="number"
                defaultValue={
                  typeof animal.data_de_nascimento !== 'number'
                    ? moment(
                        animal.data_de_nascimento
                          .toString()
                          .replace('T00:00:00.000Z', ''),
                      ).format('DD')
                    : ''
                }
                step="1"
                min="1"
                max="31"
              />
              <Input
                label="Mês"
                name="mes"
                type="number"
                defaultValue={
                  typeof animal.data_de_nascimento !== 'number'
                    ? moment(
                        animal.data_de_nascimento
                          .toString()
                          .replace('T00:00:00.000Z', ''),
                      ).format('MM')
                    : ''
                }
                step="1"
                min="1"
                max="12"
              />
              <Input
                label="Ano"
                name="ano"
                type="number"
                required={true}
                defaultValue={moment(
                  animal.data_de_nascimento
                    .toString()
                    .replace('T00:00:00.000Z', ''),
                ).format('YYYY')}
              />
            </DateInput>

            <Input
              label="Safra"
              name="safra"
              type="text"
              defaultValue={animal.safra}
            />

            <RadioInput
              direction="row"
              name="sexo"
              label="Sexo"
              options={radioOptions}
              defaultValue={animal.sexo}
              required={true}
            />

            <Input
              label="Nome"
              name="nome"
              type="text"
              defaultValue={animal.nome}
            />

            <Input
              label="Raça"
              name="ra_a"
              type="text"
              defaultValue={animal.ra_a}
            />
            <Input
              label="Categoria"
              name="categoria"
              type="text"
              defaultValue={animal.categoria}
            />
            <Input
              label="Lote"
              name="lote"
              type="text"
              defaultValue={animal.lote}
            />

            <Accordion label="Pai">
              <RelativeInput
                path="pai_id"
                property_id={id_property}
                defaultId={animal.pai?.id}
                onClear={() => {
                  history.push(`/propriedade/${id_property}/animais/novo`);
                }}
              />
            </Accordion>

            <Accordion label="Mãe">
              <RelativeInput
                path="mae_id"
                property_id={id_property}
                defaultId={animal.mae?.id}
                onClear={() => {
                  history.push(`/propriedade/${id_property}/animais/novo`);
                }}
              />
            </Accordion>

            <SwitchInput
              label="Animal pertence ao rebanho"
              name="pertencente"
              defaultChecked={animal.pertencente}
              disabled={true}
            />

            <SwitchInput
              label="Animal privado"
              name="privado"
              defaultChecked={animal.privado}
            />

            {/* <SecondaryButton type="button">
              Transferir animal <IconChevronRight size={24} />
            </SecondaryButton> */}

            <Button
              type="submit"
              isLoading={loadingAction}
              style={{ marginTop: 60 }}
            >
              Salvar
            </Button>

            <DeleteButton type="button" onClick={handleDelete}>
              <IconTrash size={25} color="#999" style={{ marginRight: 10 }} />
              Excluir
            </DeleteButton>
          </Form>
        </Content>
      )}
    </Container>
  );
};

export default EditAnimal;
